import React, { useCallback, useEffect, useMemo } from 'react';
import { Error, Cancel, OpenInNew } from '@mui/icons-material';

import { Copy, Button } from '../../../../components';
import { SHORT_LINK_BASE } from '../../hooks/useShortLink';
import { LinkArrowIcon, SaveIcon, TrashIcon } from '../../../../assets/icons';

type WebLinGeneratorProps = {
  agentId: string;
  generatedLink?: string;
  compressed?: string;
  originalUrl?: string;
  isMatched: boolean;
  setIsMatched: (value: boolean) => void;
  shortLinkForm: {
    short_link_identifier: string;
  };
  agentData?: {
    pipeline_settings?: {
      short_web_link?: string;
    };
  };
  onOpen: () => void;
  handleDeleteShortLink: (link: string) => void;
  handleUpdatehortLink: (link: string, data: { url: string }) => void;
};

export const ShortLinkGenerator: React.FC<WebLinGeneratorProps> = ({
  generatedLink,
  compressed,
  originalUrl,
  isMatched,
  setIsMatched,
  shortLinkForm,
  agentData,
  onOpen,
  handleDeleteShortLink,
  handleUpdatehortLink,
}) => {
  const shortLink = useMemo(() => {
    return agentData?.pipeline_settings?.short_web_link;
  }, [agentData?.pipeline_settings?.short_web_link]);

  const handleOpenShortLink = useCallback(() => {
    if (shortLinkForm.short_link_identifier && shortLink) {
      window.open(SHORT_LINK_BASE + shortLink, '_blank', 'noopener,noreferrer');
    }
  }, [shortLink, shortLinkForm.short_link_identifier]);

  useEffect(() => {
    if (!generatedLink || !originalUrl) return;

    if (String(generatedLink) !== String(originalUrl)) {
      setIsMatched(false);
    } else {
      setIsMatched(true);
    }
  }, [
    agentData?.pipeline_settings?.short_web_link,
    compressed,
    generatedLink,
    originalUrl,
    setIsMatched,
  ]);

  return (
    (compressed || originalUrl) && (
      <div className="flex flex-row items-center w-full pl-6">
        <div className="pb-14 relative">
          {isMatched ? (
            <LinkArrowIcon color="#A188FD" />
          ) : (
            <div className="flex items-center gap-1 text-[#F14E42]">
              <LinkArrowIcon color="#F14E42" />
              <Cancel className="absolute top-12 right-[32px] bg-white" />
            </div>
          )}
        </div>
        <div className="flex flex-row gap-x-md justify-between w-full mt-8">
          <div className="flex flex-col w-full">
            <div className="text-xl text-secondary pb-2">Short web-link</div>
            <div className="flex flex-row w-full justify-between">
              <div className="flex flex-row gap-x-sm items-center w-full">
                <div className="text-mainText text-small border border-select p-4 rounded-xl">
                  {SHORT_LINK_BASE}
                  {shortLink ?? ''}
                </div>
                <div className="flex flex-row gap-x-sm items-center">
                  <Copy
                    className="shrink-0 pl-4 text-main"
                    text={`${SHORT_LINK_BASE}${shortLink ?? ''}`}
                  />
                  {shortLink && (
                    <div className="hover:opacity-80 hover:bg-dark rounded-full p-1">
                      <Button
                        size="small"
                        onClick={() => {
                          !!shortLink && handleDeleteShortLink(shortLink);
                        }}
                        label=""
                        type="empty"
                        icon={<TrashIcon />}
                        className="text-secondary cursor-pointer hover:text-main"
                      />
                    </div>
                  )}
                  {!!shortLink && !isMatched && (
                    <Button
                      size="small"
                      type="empty"
                      className="text-main border border-border rounded-md hover:bg-contrast hover:text-main bg-gray-50 px-2 py-1"
                      onClick={() =>
                        !!shortLink &&
                        handleUpdatehortLink(shortLink, {
                          url: generatedLink || '',
                        })
                      }
                      label="Sync"
                    />
                  )}
                  {shortLinkForm.short_link_identifier && (
                    <Button
                      label=""
                      size="small"
                      type="empty"
                      icon={
                        <OpenInNew className="text-secondary hover:text-main" />
                      }
                      className="hover:bg-contrast hover:text-main bg-gray-50 text-mainText px-2 py-1"
                      onClick={handleOpenShortLink}
                    />
                  )}
                </div>
              </div>
              <div className="flex flex-row items-center w-full justify-end">
                <Button
                  label="Save to short link"
                  size="small"
                  type="empty"
                  className="border border-border rounded-md hover:bg-contrast hover:text-main bg-gray-50 text-mainText px-2 py-1"
                  icon={<SaveIcon />}
                  onClick={onOpen}
                />
              </div>
            </div>
            {!isMatched && (
              <div className="text-[#F14E42] text-tiny pt-1 flex">
                <Error className="inline-block mr-1" />
                <p>
                  Short link doesn't match the long link, consider synchronizing
                  short link again.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  );
};
